import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

interface ResourceLimitItemProps {
  label: string;
  current: number;
  max: number;
  isMobile?: boolean;
}

const ResourceLimitItem: React.FC<ResourceLimitItemProps> = ({ label, current, max, isMobile }) => {
  const percentage = Math.min(100, (current / max) * 100);
  const isAtLimit = current >= max;
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between">
        <p className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>{label}</p>
        <p className={isMobile ? "text-xs" : "text-sm"}>
          {current} / {max}
        </p>
      </div>
      <Progress 
        value={percentage} 
        className={isAtLimit ? "bg-amber-200 dark:bg-amber-900" : undefined}
        indicatorClassName={isAtLimit ? "bg-amber-500" : undefined}
      />
    </div>
  );
};

interface ResourceUsageCardProps {
  resourceUsage: any;
  packageData: any;
  resourceWarnings: string[];
}

export const ResourceUsageCard: React.FC<ResourceUsageCardProps> = ({
  resourceUsage,
  packageData,
  resourceWarnings,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  return (
    <Card>
      <CardHeader className={isMobile ? "pb-3" : ""}>
        <CardTitle className={isMobile ? "text-lg" : ""}>{t("resource_usage")}</CardTitle>
        <CardDescription className={isMobile ? "text-xs" : ""}>
          {t("resource_usage_description")}
        </CardDescription>
      </CardHeader>
      <CardContent className={`space-y-${isMobile ? "3" : "4"}`}>
        {resourceUsage && (
          <>
            <ResourceLimitItem 
              label={t("my_bots")} 
              current={resourceUsage.userBotsCount} 
              max={packageData.max_user_bots} 
              isMobile={isMobile}
            />
            
            <ResourceLimitItem 
              label={t("premium_bot_connections")} 
              current={resourceUsage.premiumBotAccountsCount} 
              max={packageData.max_premium_bots} 
              isMobile={isMobile}
            />
            
            <ResourceLimitItem 
              label={t("prop_bot_connections")} 
              current={resourceUsage.propBotAccountsCount} 
              max={packageData.max_prop_bots} 
              isMobile={isMobile}
            />
            
            <ResourceLimitItem 
              label={t("ctrader_profiles")} 
              current={resourceUsage.cspAccountsCount} 
              max={packageData.max_csp_accounts} 
              isMobile={isMobile}
            />
            
            <ResourceLimitItem 
              label={t("trading_accounts")} 
              current={resourceUsage.tradingAccountsCount} 
              max={packageData.max_trading_accounts} 
              isMobile={isMobile}
            />
          </>
        )}
        
        {resourceWarnings.length > 0 && (
          <div className={`mt-4 p-${isMobile ? "2" : "3"} bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-md`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-amber-600`} />
              <p className={`font-medium ${isMobile ? "text-xs" : ""}`}>{t("resource_limits_reached")}</p>
            </div>
            <ul className={`list-disc pl-${isMobile ? "4" : "5"} space-y-1`}>
              {resourceWarnings.map((warning, index) => (
                <li key={index} className={`${isMobile ? "text-xs" : "text-sm"}`}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
